/**
 * 多租户基础领域定义与纯函数校验。
 */

export const TENANT_ROLES = [
  "platform_super_admin",
  "tenant_admin",
  "member",
] as const;

export type TenantRole = (typeof TENANT_ROLES)[number];

export const TENANT_FEATURES = [
  "dispatch_monitor",
  "script_monitor",
  "atmosphere_assistant",
  "speech_points",
  "ai_management",
] as const;

export type TenantFeature = (typeof TENANT_FEATURES)[number];

export const TENANT_STATUSES = ["active", "disabled"] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

export type TenantFeatureFlags = Record<TenantFeature, boolean>;

export const DEFAULT_TENANT_FEATURES: TenantFeatureFlags = {
  dispatch_monitor: false,
  script_monitor: false,
  atmosphere_assistant: false,
  speech_points: false,
  ai_management: false,
};

/** 功能依赖：key 依赖 value 列表全部启用 */
export const TENANT_FEATURE_DEPENDENCIES: Partial<
  Record<TenantFeature, readonly TenantFeature[]>
> = {
  script_monitor: ["dispatch_monitor", "ai_management"],
  atmosphere_assistant: ["ai_management"],
  speech_points: ["dispatch_monitor"],
};

export const TENANT_AUDIT_ACTIONS = [
  "tenant.create",
  "tenant.update",
  "tenant.status",
  "tenant.features",
  "tenant.switch",
  "tenant_user.create",
  "tenant_user.update_role",
  "tenant_user.set_password",
  "tenant_user.ban",
  "tenant_user.unban",
] as const;

export type TenantAuditAction = (typeof TENANT_AUDIT_ACTIONS)[number];

export type TenantAuditTargetType = "system_tenant" | "user";

export class TenantFeatureDependencyError extends Error {
  readonly missing: Array<{ feature: TenantFeature; requires: TenantFeature }>;

  constructor(missing: Array<{ feature: TenantFeature; requires: TenantFeature }>) {
    const detail = missing
      .map((item) => `${item.feature} 依赖 ${item.requires}`)
      .join("；");
    super(`非法功能组合：${detail}`);
    this.name = "TenantFeatureDependencyError";
    this.missing = missing;
  }
}

export function isTenantRole(value: unknown): value is TenantRole {
  return typeof value === "string" && (TENANT_ROLES as readonly string[]).includes(value);
}

export function isTenantFeature(value: unknown): value is TenantFeature {
  return typeof value === "string" && (TENANT_FEATURES as readonly string[]).includes(value);
}

export function isTenantStatus(value: unknown): value is TenantStatus {
  return typeof value === "string" && (TENANT_STATUSES as readonly string[]).includes(value);
}

/** admin 是平台超管的归属租户，在没有独立平台身份恢复机制前不可停用。 */
export function isProtectedAdminTenantCode(code: string | null | undefined): boolean {
  return normalizeTenantCode(code ?? "") === "admin";
}

export function isPlatformSuperAdmin(role: TenantRole | string | null | undefined): boolean {
  return role === "platform_super_admin";
}

export function isTenantAdmin(role: TenantRole | string | null | undefined): boolean {
  return role === "tenant_admin" || role === "platform_super_admin";
}

/**
 * 兼容迁移：legacy Better Auth role=admin 仅作为过渡判断，
 * 平台超级管理员最终以 tenantRole 为准。
 */
export function isLegacyAdminRole(role: string | null | undefined): boolean {
  return role === "admin";
}

export function createDefaultTenantFeatures(
  partial?: Partial<TenantFeatureFlags>
): TenantFeatureFlags {
  return {
    ...DEFAULT_TENANT_FEATURES,
    ...(partial ?? {}),
  };
}

export function normalizeTenantFeatures(
  input?: Partial<TenantFeatureFlags> | null
): TenantFeatureFlags {
  const features = createDefaultTenantFeatures(input ?? undefined);
  assertValidTenantFeatureDependencies(features);
  return features;
}

export function collectTenantFeatureDependencyErrors(
  features: Partial<TenantFeatureFlags> | TenantFeatureFlags
): Array<{ feature: TenantFeature; requires: TenantFeature }> {
  const missing: Array<{ feature: TenantFeature; requires: TenantFeature }> = [];
  for (const feature of TENANT_FEATURES) {
    if (!features[feature]) continue;
    const deps = TENANT_FEATURE_DEPENDENCIES[feature] ?? [];
    for (const dep of deps) {
      if (!features[dep]) {
        missing.push({ feature, requires: dep });
      }
    }
  }
  return missing;
}

export function assertValidTenantFeatureDependencies(
  features: Partial<TenantFeatureFlags> | TenantFeatureFlags
): void {
  const missing = collectTenantFeatureDependencyErrors(features);
  if (missing.length > 0) {
    throw new TenantFeatureDependencyError(missing);
  }
}

export function mergeTenantFeatures(
  current: TenantFeatureFlags,
  patch: Partial<TenantFeatureFlags>
): TenantFeatureFlags {
  return normalizeTenantFeatures({
    ...current,
    ...patch,
  });
}

export function hasTenantFeature(
  features: Partial<TenantFeatureFlags> | TenantFeatureFlags | null | undefined,
  feature: TenantFeature
): boolean {
  return Boolean(features?.[feature]);
}

export function canManageTenantUsers(role: TenantRole | string | null | undefined): boolean {
  return role === "platform_super_admin" || role === "tenant_admin";
}

/**
 * 解析租户用户管理作用域：
 * - 平台超管：当前 active tenant
 * - 租户管理员：自身 home tenant
 * - 其他：无权限（返回 null）
 */
export function resolveManagedSystemTenantId(input: {
  actorRole: TenantRole | string | null | undefined;
  homeSystemTenantId: string;
  activeSystemTenantId: string;
}): string | null {
  if (!canManageTenantUsers(input.actorRole)) {
    return null;
  }
  if (isPlatformSuperAdmin(input.actorRole)) {
    return input.activeSystemTenantId;
  }
  return input.homeSystemTenantId;
}

export function canSwitchTenant(role: TenantRole | string | null | undefined): boolean {
  return isPlatformSuperAdmin(role);
}

/**
 * 解析 session 上的 active tenant：
 * - 平台超管：默认自身 home tenant，可切换
 * - 其他用户：只能是自身 home tenant
 */
export function resolveActiveSystemTenantId(input: {
  tenantRole: TenantRole | string | null | undefined;
  homeSystemTenantId: string;
  sessionActiveSystemTenantId?: string | null;
}): string {
  const home = input.homeSystemTenantId;
  if (!home) {
    throw new Error("用户缺少 systemTenantId");
  }
  if (!isPlatformSuperAdmin(input.tenantRole)) {
    return home;
  }
  return input.sessionActiveSystemTenantId?.trim() || home;
}

export function assertCanAccessTenantResource(input: {
  tenantRole: TenantRole | string | null | undefined;
  homeSystemTenantId: string;
  activeSystemTenantId: string;
  resourceSystemTenantId: string;
}): void {
  if (isPlatformSuperAdmin(input.tenantRole)) {
    if (input.resourceSystemTenantId !== input.activeSystemTenantId) {
      // 平台超管只能操作当前 active tenant 的业务资源
      const error = new Error("NOT_FOUND");
      error.name = "TenantResourceNotFoundError";
      throw error;
    }
    return;
  }
  if (
    input.resourceSystemTenantId !== input.homeSystemTenantId ||
    input.activeSystemTenantId !== input.homeSystemTenantId
  ) {
    const error = new Error("NOT_FOUND");
    error.name = "TenantResourceNotFoundError";
    throw error;
  }
}

export type TenantUserGuardInput = {
  actorUserId: string;
  actorRole: TenantRole;
  targetUserId: string;
  targetRole: TenantRole;
  nextRole?: TenantRole;
  action: "update_role" | "ban" | "unban" | "set_password";
  tenantAdminCount: number;
};

export type TenantUserGuardResult =
  | { ok: true }
  | { ok: false; code: string; message: string };

/**
 * 租户用户管理安全规则（纯函数）。
 */
export function evaluateTenantUserMutation(
  input: TenantUserGuardInput
): TenantUserGuardResult {
  // 平台超管身份只允许迁移/bootstrap 管理，禁止任何 tenantUser 路径操作（含超管互 ban/改密）。
  if (input.targetRole === "platform_super_admin") {
    return {
      ok: false,
      code: "FORBIDDEN",
      message: "禁止通过租户用户管理操作平台超级管理员",
    };
  }

  if (input.nextRole === "platform_super_admin") {
    return { ok: false, code: "FORBIDDEN", message: "禁止创建或提升为平台超级管理员" };
  }

  if (input.action === "ban" && input.actorUserId === input.targetUserId) {
    return { ok: false, code: "BAD_REQUEST", message: "不能停用自己" };
  }

  if (input.action === "update_role" && input.actorUserId === input.targetUserId) {
    // 允许改自己的非管理员降级？不允许把自己从最后一个 tenant_admin 降级
    // 其他角色改自己也无业务意义，统一拒绝
    return { ok: false, code: "BAD_REQUEST", message: "不能变更自己的角色" };
  }

  const demotingLastAdmin =
    input.targetRole === "tenant_admin" &&
    input.tenantAdminCount <= 1 &&
    ((input.action === "update_role" && input.nextRole !== "tenant_admin") ||
      input.action === "ban");

  if (demotingLastAdmin) {
    return {
      ok: false,
      code: "BAD_REQUEST",
      message: "不能停用或降级最后一个租户管理员",
    };
  }

  return { ok: true };
}

export function buildGeneratedUserEmail(username: string): string {
  return `${username.trim().toLowerCase()}@local.dev`;
}

export function normalizeTenantCode(code: string): string {
  return code.trim().toLowerCase();
}

/**
 * 解析 SYSTEM_TENANCY_PLATFORM_ADMIN_USERS 逗号列表。
 * 支持 username / email / id 精确匹配（大小写不敏感对 username/email）。
 */
export function parsePlatformAdminSelectors(raw: string | null | undefined): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const part of raw.split(",")) {
    const value = part.trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(value);
  }
  return result;
}

export type PlatformAdminCandidate = {
  id?: string | null;
  username?: string | null;
  email?: string | null;
  role?: string | null;
  tenantRole?: string | null;
};

export type PlatformAdminSelectionResult =
  | {
      ok: true;
      selectedIds: string[];
      selected: PlatformAdminCandidate[];
    }
  | {
      ok: false;
      code: string;
      message: string;
      selectedIds: string[];
    };

/**
 * 从 legacy admin / 候选用户中按显式 selector 选出平台超管。
 * 默认期望恰好 expectedCount 个（默认 2）；未匹配或数量不符则失败。
 * 其余 legacy admin 应降为 member（由调用方写入）。
 */
export function selectPlatformSuperAdmins(input: {
  candidates: PlatformAdminCandidate[];
  selectors: string[];
  expectedCount?: number;
}): PlatformAdminSelectionResult {
  const expectedCount =
    typeof input.expectedCount === "number" && Number.isFinite(input.expectedCount)
      ? Math.floor(input.expectedCount)
      : 2;

  if (expectedCount < 1) {
    return {
      ok: false,
      code: "INVALID_EXPECTED_COUNT",
      message: "expectedCount 必须 >= 1",
      selectedIds: [],
    };
  }

  if (input.selectors.length === 0) {
    return {
      ok: false,
      code: "MISSING_PLATFORM_ADMIN_SELECTORS",
      message:
        "必须通过 SYSTEM_TENANCY_PLATFORM_ADMIN_USERS 显式指定平台超级管理员（username/email/id 逗号列表）",
      selectedIds: [],
    };
  }

  if (input.selectors.length !== expectedCount) {
    return {
      ok: false,
      code: "PLATFORM_ADMIN_SELECTOR_COUNT",
      message: `SYSTEM_TENANCY_PLATFORM_ADMIN_USERS 需要恰好 ${expectedCount} 个唯一标识，当前 ${input.selectors.length} 个`,
      selectedIds: [],
    };
  }

  const selected: PlatformAdminCandidate[] = [];
  const selectedIds: string[] = [];
  const unmatched: string[] = [];

  for (const selector of input.selectors) {
    const needle = selector.trim().toLowerCase();
    const matches = input.candidates.filter((user) => {
      const id = String(user.id ?? "").trim();
      const username = String(user.username ?? "").trim().toLowerCase();
      const email = String(user.email ?? "").trim().toLowerCase();
      return (
        (id && id.toLowerCase() === needle) ||
        (username && username === needle) ||
        (email && email === needle)
      );
    });

    if (matches.length === 0) {
      unmatched.push(selector);
      continue;
    }
    if (matches.length > 1) {
      return {
        ok: false,
        code: "PLATFORM_ADMIN_SELECTOR_AMBIGUOUS",
        message: `平台超级管理员标识匹配到多个用户：${selector}`,
        selectedIds,
      };
    }

    const user = matches[0]!;
    const id = String(user.id ?? "").trim();
    if (!id) {
      return {
        ok: false,
        code: "PLATFORM_ADMIN_MISSING_ID",
        message: `匹配到的用户缺少 id：${selector}`,
        selectedIds,
      };
    }
    if (selectedIds.includes(id)) {
      return {
        ok: false,
        code: "PLATFORM_ADMIN_DUPLICATE_USER",
        message: `平台超级管理员标识重复指向同一用户：${selector}`,
        selectedIds,
      };
    }
    selectedIds.push(id);
    selected.push(user);
  }

  if (unmatched.length > 0) {
    return {
      ok: false,
      code: "PLATFORM_ADMIN_SELECTOR_UNMATCHED",
      message: `未匹配到平台超级管理员：${unmatched.join(", ")}`,
      selectedIds,
    };
  }

  if (selectedIds.length !== expectedCount) {
    return {
      ok: false,
      code: "PLATFORM_ADMIN_COUNT",
      message: `平台超级管理员数量必须恰好为 ${expectedCount}，实际 ${selectedIds.length}`,
      selectedIds,
    };
  }

  return { ok: true, selectedIds, selected };
}

/**
 * Better Auth /admin 路径外部请求一律拒绝；仅无 request/headers 的服务端内部调用放行。
 */
export function shouldDenyExternalBetterAuthAdminPath(input: {
  path: string;
  hasExternalRequest: boolean;
}): boolean {
  if (!input.path.startsWith("/admin")) {
    return false;
  }
  return input.hasExternalRequest;
}

/**
 * 租户停用时需要吊销的 session 过滤：
 * - 该租户全部用户的 session（userId in tenantUserIds）
 * - 任意 activeSystemTenantId 指向该租户的 session（含平台超管切换后的会话）
 */
export function buildTenantDisableSessionRevokeFilter(input: {
  tenantUserIds: string[];
  systemTenantId: string;
}): Record<string, unknown> {
  const userIds = [...new Set(input.tenantUserIds.filter(Boolean))];
  const or: Record<string, unknown>[] = [
    { activeSystemTenantId: input.systemTenantId },
  ];
  if (userIds.length > 0) {
    or.unshift({ userId: { $in: userIds } });
  }
  return { $or: or };
}

export function buildUserSessionRevokeFilter(userId: string): { userId: string } {
  return { userId };
}
